import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { evaluationService } from '@/services/evaluationService'
import type { TestCaseWithEvaluations } from '@shared/types/evaluation'
import { toast } from 'sonner'

// Validation schema
const testCaseSchema = z.object({
  question: z.string().min(1, 'Question is required').max(2000, 'Question must be less than 2000 characters'),
  goldenAnswer: z.string().min(1, 'Expected answer is required').max(5000, 'Expected answer must be less than 5000 characters'),
  category: z.string().optional(),
  difficulty: z.enum(['easy', 'medium', 'hard']).optional(),
  tags: z.array(z.string()).optional()
})

type TestCaseFormData = z.infer<typeof testCaseSchema>

interface EditTestCaseModalProps {
  testSetId: string
  testCase: TestCaseWithEvaluations | null
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export const EditTestCaseModal: React.FC<EditTestCaseModalProps> = ({
  testSetId,
  testCase,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [newTag, setNewTag] = useState('')
  const [tags, setTags] = useState<string[]>([])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<TestCaseFormData>({
    resolver: zodResolver(testCaseSchema),
    defaultValues: {
      question: '',
      goldenAnswer: '',
      category: '',
      difficulty: undefined,
      tags: []
    }
  })

  useEffect(() => {
    if (testCase && isOpen) {
      reset({
        question: testCase.question || '',
        goldenAnswer: testCase.goldenAnswer || '',
        category: testCase.category || '',
        difficulty: testCase.difficulty || undefined,
        tags: testCase.tags || []
      })
      setTags(testCase.tags || [])
    }
  }, [testCase, isOpen, reset])

  const handleClose = () => {
    reset()
    setTags([])
    setNewTag('')
    onClose()
  }

  const onSubmit = async (data: TestCaseFormData) => {
    if (!testCase) return

    try {
      setIsSubmitting(true)
      
      const testCaseData = {
        question: data.question,
        goldenAnswer: data.goldenAnswer,
        category: data.category && data.category.trim() !== '' ? data.category : undefined,
        difficulty: data.difficulty || undefined,
        tags: tags.length > 0 ? tags : undefined
      }

      await evaluationService.updateTestCase(testSetId, testCase.id, testCaseData)
      toast.success('Test case updated successfully')
      onSuccess?.()
      handleClose()
    } catch (error) {
      console.error('Error updating test case:', error)
      toast.error('Failed to update test case')
    } finally {
      setIsSubmitting(false)
    }
  }

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      const updatedTags = [...tags, newTag.trim()]
      setTags(updatedTags)
      setValue('tags', updatedTags)
      setNewTag('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    const updatedTags = tags.filter(tag => tag !== tagToRemove)
    setTags(updatedTags)
    setValue('tags', updatedTags)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag()
    }
  }

  if (!testCase) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Test Case</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Question */}
          <div className="space-y-2">
            <Label htmlFor="question">
              Question <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="question"
              placeholder="Enter the test question..."
              className="min-h-[100px]"
              {...register('question')}
            />
            {errors.question && (
              <p className="text-sm text-red-500">{errors.question.message}</p>
            )}
          </div>

          {/* Golden Answer */}
          <div className="space-y-2">
            <Label htmlFor="goldenAnswer">
              Expected Answer <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="goldenAnswer"
              placeholder="Enter the expected/golden answer..."
              className="min-h-[150px]"
              {...register('goldenAnswer')}
            />
            {errors.goldenAnswer && (
              <p className="text-sm text-red-500">{errors.goldenAnswer.message}</p>
            )}
          </div>

          {/* Category and Difficulty */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                placeholder="e.g., HR Policy, Benefits, etc."
                {...register('category')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="difficulty">Difficulty</Label>
              <Select 
                value={watch('difficulty') || ''} 
                onValueChange={(value) => setValue('difficulty', value as 'easy' | 'medium' | 'hard')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select difficulty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Add a tag..."
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTag}
                disabled={!newTag.trim()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="gap-1">
                    {tag}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 hover:bg-transparent"
                      onClick={() => removeTag(tag)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Evaluation History */}
          {testCase.evaluations && testCase.evaluations.length > 0 && (
            <div className="space-y-2">
              <Label>Evaluation History</Label>
              <div className="bg-muted p-3 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  This test case has been evaluated {testCase.evaluations.length} time(s).
                  Last evaluation score: {testCase.evaluations[0]?.judgeScore?.toFixed(2) || 'N/A'}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Test Case'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}